import { useForm } from 'react-hook-form';
import { FormProvider, Table, TextField, Checkbox, SelectTag } from '../../../../../../components';
import { Button, Container, Dialog, IconButton, Paper, Stack, Typography, Box, FormControlLabel, Grid } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { request, toast } from '../../../../../../utils';
import { useLocation } from 'react-router-dom';
import { CustomButtonn } from '../../../../../../components';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';

const TABLE_COLUMNS = [
  {
    header: 'Code',
    accessor: 'code'
  },
  {
    header: 'Department',
    accessor: (row) => {
      const status = row.status;
      return (
        <span
          style={{
            color: status === 'inactive' ? 'red' : 'black',
          }}
        >
          {`${row.department}`}
        </span>
      );
    },
  },
  {
    header: 'Short Name',
    accessor: 'shortName'
  },
  {
    header: 'Type',
    accessor: 'type',
    sx: { textTransform: 'uppercase' }
  },
  {
    header: 'Status',
    accessor: 'status',
    sx: { textTransform: 'uppercase' }
  },
  {
    header: 'Location',
    accessor: 'location'
  },
  {
    header: 'Contact',
    accessor: 'contactNumber'
  },
  {
    header: 'Created At',
    accessor: 'createdAt'
  }
];

// Department type options
const DEPARTMENT_TYPES = [
  { _id: 'CLINICAL', name: 'Clinical' },
  { _id: 'NON_CLINICAL', name: 'Non-Clinical' },
  { _id: 'SUPPORT', name: 'Support' },
  { _id: 'ADMIN', name: 'Administrative' }
];

// Icon options (Material Icons)
const ICON_OPTIONS = [
  { _id: 'business', name: 'Business' },
  { _id: 'local_hospital', name: 'Hospital' },
  { _id: 'medical_services', name: 'Medical Services' },
  { _id: 'science', name: 'Science' },
  { _id: 'school', name: 'School' },
  { _id: 'account_balance', name: 'Administration' },
  { _id: 'support', name: 'Support' },
  { _id: 'engineering', name: 'Engineering' }
];

// Color options
const COLOR_OPTIONS = [
  { _id: '#1976d2', name: 'Blue' },
  { _id: '#2e7d32', name: 'Green' },
  { _id: '#ed6c02', name: 'Orange' },
  { _id: '#d32f2f', name: 'Red' },
  { _id: '#7b1fa2', name: 'Purple' },
  { _id: '#0288d1', name: 'Light Blue' },
  { _id: '#388e3c', name: 'Light Green' },
  { _id: '#f57c00', name: 'Dark Orange' }
];

const initialDepartmentDataObj = {
  data: [],
  loading: false
};

export const DepartmentMaster = () => {
  const [openQueryDialog, setOpenQueryDialog] = useState(false);
  const [departmentDataObj, setDepartmentDataObj] = useState(initialDepartmentDataObj);
  const [loading, setLoading] = useState(false);
  const [currentAction, setCurrentAction] = useState();
  const [selectedRecord, setSelectedRecord] = useState();
  const [doctorList, setDoctorList] = useState([]);
  const [userList, setUserList] = useState([]);
  const { pathname } = useLocation();
  const lastPathSegment = pathname.split('/').pop();

  const methods = useForm({
    resolver: yupResolver(
      Yup.object().shape({
        department: Yup.string().required('Department is required'),
        type: Yup.string().required('Department type is required'),
        code: Yup.string(),
        shortName: Yup.string(),
        status: Yup.string().required('Status is required'),
        location: Yup.string(),
        contactNumber: Yup.string(),
        email: Yup.string().email('Invalid email format'),
        serviceHoursFrom: Yup.string(),
        serviceHoursTo: Yup.string(),
        colorCode: Yup.string(),
        icon: Yup.string()
      })
    ),
    defaultValues: { 
      code: '',
      department: '', 
      shortName: '',
      type: 'CLINICAL',
      status: 'active',
      location: '',
      contactNumber: '',
      email: '',
      serviceHoursFrom: '09:00',
      serviceHoursTo: '17:00',
      colorCode: '#1976d2',
      icon: 'business',
      headOfDepartment: '',
      departmentIncharge: ''
    },
    mode: 'all'
  });
  const { handleSubmit, setValue, reset, watch, control } = methods;

  // Watch values
  const statusValue = watch('status');
  const colorValue = watch('colorCode');
  const iconValue = watch('icon');

  useEffect(() => {
    if (selectedRecord) {
      // Set all form values from selected record
      Object.keys(methods.getValues()).forEach(key => {
        if (selectedRecord[key] !== undefined) {
          setValue(key, selectedRecord[key]);
        }
      });
      
      // Handle nested serviceHours
      if (selectedRecord.serviceHours) {
        setValue('serviceHoursFrom', selectedRecord.serviceHours.from || '09:00');
        setValue('serviceHoursTo', selectedRecord.serviceHours.to || '17:00');
      }
    } else {
      reset();
    }
  }, [setValue, reset, selectedRecord, methods]);

  // Fetch doctor and user lists
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        // Fetch doctors for headOfDepartment
        const doctorsRes = await request('/doctors-list', { method: 'GET' });
        if (doctorsRes.success) {
          setDoctorList(doctorsRes.data.map(doc => ({
            _id: doc._id,
            name: `${doc.firstName} ${doc.lastName}`
          })));
        }

        // Fetch users for departmentIncharge
        const usersRes = await request('/users-list', { method: 'GET' });
        if (usersRes.success) {
          setUserList(usersRes.data.map(user => ({
            _id: user._id,
            name: user.username || user.email
          })));
        }
      } catch (error) {
        console.error('Error fetching dropdown data:', error);
      }
    };

    fetchDropdownData();
  }, []);

const formSubmit = async (formValues) => {
  setLoading(true);
  let editFlag = false;
  if (selectedRecord && currentAction === 'edit') {
    editFlag = true;
  }
  
  // Prepare serviceHours object
  const serviceHours = {
    from: formValues.serviceHoursFrom || '09:00',
    to: formValues.serviceHoursTo || '17:00'
  };

  // Prepare request body - handle empty strings for ObjectId fields
  const requestBody = {
    code: formValues.code,
    department: formValues.department,
    shortName: formValues.shortName,
    type: formValues.type,
    status: formValues.status,
    // Only include headOfDepartment if it has a value
    headOfDepartment: formValues.headOfDepartment || null,
    // Only include departmentIncharge if it has a value
    departmentIncharge: formValues.departmentIncharge || null,
    location: formValues.location,
    contactNumber: formValues.contactNumber,
    email: formValues.email,
    serviceHours: serviceHours,
    colorCode: formValues.colorCode,
    icon: formValues.icon
  };

  // Clean up the request body - remove null/empty values if needed
  Object.keys(requestBody).forEach(key => {
    if (requestBody[key] === null || requestBody[key] === '') {
      delete requestBody[key];
    }
  });

  const response = await request(editFlag ? '/department-update' : '/department-create', {
    method: editFlag ? 'PUT' : 'POST',
    params: editFlag ? selectedRecord._id : null,
    body: requestBody
  });
  
  if (response?.success) {
    toast(`Successfully ${editFlag ? 'updated' : 'created'} department master`, { variant: 'success' });
    setSelectedRecord(response.data);
    setCurrentAction();
  } else {
    toast(`Failed to ${editFlag ? 'edit' : 'create'} department master`, { variant: 'error' });
  }
  setLoading(false);
};

  const handleCancelRecord = async () => {
    if (!selectedRecord) return;
    
    setLoading(true);
    const response = await request('/department-cancel', {
      method: 'DELETE',
      params: selectedRecord._id
    });
    
    if (response?.success) {
      toast('Successfully cancelled department master', { variant: 'success' });
      setSelectedRecord();
      reset();
    } else {
      toast('Failed to cancel department master', { variant: 'error' });
    }
    setLoading(false);
  };

  const handleDeleteRecord = async () => {
    if (!selectedRecord) return;
    
    setLoading(true);
    const response = await request('/department-delete', {
      method: 'DELETE',
      params: selectedRecord._id
    });
    
    if (response?.success) {
      toast('Successfully deleted department master', { variant: 'success' });
      setSelectedRecord();
      reset();
    } else {
      toast('Failed to delete department master', { variant: 'error' });
    }
    setLoading(false);
  };

  const handleQueryWindow = async () => {
    setOpenQueryDialog(true);
    setDepartmentDataObj({ loading: true });
    
    const response = await request('/department-list', { method: 'GET' });
    if (response.success) {
      const filteredData = response.data.filter(item =>
        item.status === 'active' || item.status === 'inactive' || item.status === 'default'
      );
      setDepartmentDataObj({ loading: false, data: filteredData });
    } else {
      toast('Error in fetching data', { variant: 'error' });
      setDepartmentDataObj({ loading: false });
    }
  };

  const handleTimeChange = (field, time) => {
    setValue(field, time ? dayjs(time).format('HH:mm') : '');
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Paper sx={{
        p: 0, flex: 1, borderRadius: 0, boxShadow: 'none',
        width: '100%',
        height: 'auto'
      }}>
        <Container
          sx={{
            mt: 1,
            border: '0px solid',
            borderColor: 'grey.300',
            borderRadius: 1,
            width: '100%',
            maxWidth: 'none',
            minWidth: "100%",
            boxSizing: 'border-box',
          }}
        >
          <FormProvider methods={methods} onSubmit={handleSubmit(formSubmit)}>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="space-between"
              alignItems="center"
              sx={{
                mb: 1,
                borderBottom: '0px solid',
                borderColor: 'grey.300'
              }}
            >
              <Typography color="primary" variant="h3" sx={{ fontWeight: 600 }}>
                Department Master
              </Typography>
              <Stack direction="row" justifyContent="flex-end" sx={{ mb: 3 }} spacing={1}>
                <CustomButtonn
                  id={`${lastPathSegment}Write`}
                  variant="contained"
                  disabled={['edit', 'new'].includes(currentAction)}
                  onClick={() => {
                    setSelectedRecord();
                    setCurrentAction('new');
                    reset({
                      code: '',
                      department: '', 
                      shortName: '',
                      type: 'CLINICAL',
                      status: 'active',
                      location: '',
                      contactNumber: '',
                      email: '',
                      serviceHoursFrom: '09:00',
                      serviceHoursTo: '17:00',
                      colorCode: '#1976d2',
                      icon: 'business',
                      headOfDepartment: '',
                      departmentIncharge: ''
                    });
                  }}
                >
                  New
                </CustomButtonn>
                <CustomButtonn
                  id={`${lastPathSegment}Update`}
                  variant="contained"
                  disabled={['new', 'edit'].includes(currentAction) || !selectedRecord}
                  onClick={() => setCurrentAction('edit')}
                >
                  Edit
                </CustomButtonn>
                <CustomButtonn
                  id={"Submit"}
                  type="submit"
                  variant="contained"
                  disabled={!['edit', 'new'].includes(currentAction) || loading}
                >
                  Save
                </CustomButtonn>
                <CustomButtonn
                  id={"Cancel"}
                  variant="contained"
                  disabled={['edit', 'new'].includes(currentAction) || loading}
                  onClick={() => {
                    setCurrentAction();
                    handleCancelRecord()
                  }}
                >
                  Cancel
                </CustomButtonn>
                <CustomButtonn
                  id={`isSuperAdmin`}
                  variant="contained"
                  disabled={!selectedRecord || ['new', 'edit'].includes(currentAction) || loading}
                  onClick={handleDeleteRecord}
                >
                  Delete
                </CustomButtonn>
                <CustomButtonn
                  id={`${lastPathSegment}Read`}
                  variant="contained"
                  onClick={handleQueryWindow}
                  disabled={['new', 'edit'].includes(currentAction)}
                >
                  Query
                </CustomButtonn>
                <CustomButtonn id={`${lastPathSegment}Print`} variant="contained" disabled={!selectedRecord || currentAction}>
                  Print
                </CustomButtonn>
                <CustomButtonn
                  id={"Exit"}
                  variant="contained"
                  onClick={() => {
                    setCurrentAction('');
                  }}
                >
                  Exit
                </CustomButtonn>
              </Stack>
            </Stack>
            
            <Grid container spacing={2} sx={{ marginBottom: "1%" }}>
              {/* Row 1: Code and Department */}
              <Grid item xs={6}>
                <Box sx={{ 
                  border: '1px solid gray', 
                  padding: 2, 
                  borderRadius: '8px',
                  boxShadow: '0px 2px 10px #0099ff' 
                }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Typography sx={{ flex: 0.4, fontSize: '1.2rem', fontWeight: 700 }}>
                      Code
                    </Typography>
                    <TextField
                      name="code"
                      sx={{ 
                        flex: 1,
                        '& .MuiInputBase-input': { fontSize: '1.2rem', fontWeight: 600 }
                      }}
                      placeholder="Auto-generated or enter code"
                      fullWidth
                      disabled={!['new', 'edit'].includes(currentAction)}
                    />
                  </Stack>
                </Box>
              </Grid>

              <Grid item xs={6}>
                <Box sx={{ 
                  border: '1px solid gray', 
                  padding: 2, 
                  borderRadius: '8px',
                  boxShadow: '0px 2px 10px #0099ff' 
                }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Typography sx={{ flex: 0.4, fontSize: '1.2rem', fontWeight: 700 }}>
                      Department Name*
                    </Typography>
                    <TextField
                      name="department"
                      sx={{ 
                        flex: 1,
                        '& .MuiInputBase-input': { fontSize: '1.2rem', fontWeight: 600 }
                      }}
                      placeholder="Enter Department Name"
                      fullWidth
                      disabled={!['new', 'edit'].includes(currentAction)}
                    />
                  </Stack>
                </Box>
              </Grid>

              {/* Row 2: Short Name and Type */}
              <Grid item xs={6}>
                <Box sx={{ 
                  border: '1px solid gray', 
                  padding: 2, 
                  borderRadius: '8px',
                  boxShadow: '0px 2px 10px #0099ff' 
                }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Typography sx={{ flex: 0.4, fontSize: '1.2rem', fontWeight: 700 }}>
                      Short Name
                    </Typography>
                    <TextField
                      name="shortName"
                      sx={{ 
                        flex: 1,
                        '& .MuiInputBase-input': { fontSize: '1.2rem', fontWeight: 600 }
                      }}
                      placeholder="Enter short name"
                      fullWidth
                      disabled={!['new', 'edit'].includes(currentAction)}
                    />
                  </Stack>
                </Box>
              </Grid>

              <Grid item xs={6}>
                <Box sx={{ 
                  border: '1px solid gray', 
                  padding: 2, 
                  borderRadius: '8px',
                  boxShadow: '0px 2px 10px #0099ff' 
                }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Typography sx={{ flex: 0.4, fontSize: '1.2rem', fontWeight: 700 }}>
                      Department Type*
                    </Typography>
                    <SelectTag
                      name="type"
                      menus={DEPARTMENT_TYPES}
                      sx={{ 
                        flex: 1,
                        '& .MuiInputBase-input': { fontSize: '1.2rem', fontWeight: 600 }
                      }}
                      placeholder="Select Department Type"
                      disabled={!['new', 'edit'].includes(currentAction)}
                      allowClear={false}
                    />
                  </Stack>
                </Box>
              </Grid>

              {/* Row 3: Head of Department and Incharge */}
              <Grid item xs={6}>
                <Box sx={{ 
                  border: '1px solid gray', 
                  padding: 2, 
                  borderRadius: '8px',
                  boxShadow: '0px 2px 10px #0099ff' 
                }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Typography sx={{ flex: 0.4, fontSize: '1.2rem', fontWeight: 700 }}>
                      Head of Department
                    </Typography>
                    <SelectTag
                      name="headOfDepartment"
                      menus={doctorList}
                      sx={{ 
                        flex: 1,
                        '& .MuiInputBase-input': { fontSize: '1.2rem', fontWeight: 600 }
                      }}
                      placeholder="Select Head of Department"
                      disabled={!['new', 'edit'].includes(currentAction)}
                      allowClear={true}
                    />
                  </Stack>
                </Box>
              </Grid>

              <Grid item xs={6}>
                <Box sx={{ 
                  border: '1px solid gray', 
                  padding: 2, 
                  borderRadius: '8px',
                  boxShadow: '0px 2px 10px #0099ff' 
                }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Typography sx={{ flex: 0.4, fontSize: '1.2rem', fontWeight: 700 }}>
                      Department Incharge
                    </Typography>
                    <SelectTag
                      name="departmentIncharge"
                      menus={userList}
                      sx={{ 
                        flex: 1,
                        '& .MuiInputBase-input': { fontSize: '1.2rem', fontWeight: 600 }
                      }}
                      placeholder="Select Department Incharge"
                      disabled={!['new', 'edit'].includes(currentAction)}
                      allowClear={true}
                    />
                  </Stack>
                </Box>
              </Grid>

              {/* Row 4: Location and Contact */}
              <Grid item xs={6}>
                <Box sx={{ 
                  border: '1px solid gray', 
                  padding: 2, 
                  borderRadius: '8px',
                  boxShadow: '0px 2px 10px #0099ff' 
                }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Typography sx={{ flex: 0.4, fontSize: '1.2rem', fontWeight: 700 }}>
                      Location
                    </Typography>
                    <TextField
                      name="location"
                      sx={{ 
                        flex: 1,
                        '& .MuiInputBase-input': { fontSize: '1.2rem', fontWeight: 600 }
                      }}
                      placeholder="Enter location"
                      fullWidth
                      disabled={!['new', 'edit'].includes(currentAction)}
                    />
                  </Stack>
                </Box>
              </Grid>

              <Grid item xs={6}>
                <Box sx={{ 
                  border: '1px solid gray', 
                  padding: 2, 
                  borderRadius: '8px',
                  boxShadow: '0px 2px 10px #0099ff' 
                }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Typography sx={{ flex: 0.4, fontSize: '1.2rem', fontWeight: 700 }}>
                      Contact Number
                    </Typography>
                    <TextField
                      name="contactNumber"
                      sx={{ 
                        flex: 1,
                        '& .MuiInputBase-input': { fontSize: '1.2rem', fontWeight: 600 }
                      }}
                      placeholder="Enter contact number"
                      fullWidth
                      disabled={!['new', 'edit'].includes(currentAction)}
                    />
                  </Stack>
                </Box>
              </Grid>

              {/* Row 5: Email and Status */}
              <Grid item xs={6}>
                <Box sx={{ 
                  border: '1px solid gray', 
                  padding: 2, 
                  borderRadius: '8px',
                  boxShadow: '0px 2px 10px #0099ff' 
                }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Typography sx={{ flex: 0.4, fontSize: '1.2rem', fontWeight: 700 }}>
                      Email
                    </Typography>
                    <TextField
                      name="email"
                      type="email"
                      sx={{ 
                        flex: 1,
                        '& .MuiInputBase-input': { fontSize: '1.2rem', fontWeight: 600 }
                      }}
                      placeholder="Enter email address"
                      fullWidth
                      disabled={!['new', 'edit'].includes(currentAction)}
                    />
                  </Stack>
                </Box>
              </Grid>

              <Grid item xs={6}>
                <Box sx={{ 
                  border: '1px solid gray', 
                  padding: 2, 
                  borderRadius: '8px',
                  width: '100%',
                  boxShadow: '0px 2px 10px #0099ff',
                  backgroundColor: statusValue === 'inactive' ? '#ffe6e6' : 'white'
                }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Typography sx={{ flex: 0.4, fontSize: '1.2rem', fontWeight: 700 }}>
                      Status*
                    </Typography>
                    <Stack direction="row" spacing={3} sx={{ flex: 1 }}>
                      <Stack direction="row" alignItems="center">
                        <Checkbox
                          name="status"
                          value="active"
                          checked={statusValue === 'active'}
                          onChange={(e) => setValue('status', e.target.checked ? 'active' : 'inactive')}
                          disabled={!['new', 'edit'].includes(currentAction)}
                          sx={{ '& .MuiSvgIcon-root': { fontSize: 28 } }}
                        />
                        <Typography sx={{ fontSize: '1.2rem', fontWeight: 600 }}>Active</Typography>
                      </Stack>
                      <Stack direction="row" alignItems="center">
                        <Checkbox
                          name="status"
                          value="inactive"
                          checked={statusValue === 'inactive'}
                          onChange={(e) => setValue('status', e.target.checked ? 'inactive' : 'active')}
                          disabled={!['new', 'edit'].includes(currentAction)}
                          sx={{ '& .MuiSvgIcon-root': { fontSize: 28 } }}
                        />
                        <Typography sx={{ fontSize: '1.2rem', fontWeight: 600 }}>Inactive</Typography>
                      </Stack>
                    </Stack>
                  </Stack>
                </Box>
              </Grid>

              {/* Row 6: Service Hours */}
              <Grid item xs={6}>
                <Box sx={{ 
                  border: '1px solid gray', 
                  padding: 2, 
                  borderRadius: '8px',
                  boxShadow: '0px 2px 10px #0099ff' 
                }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Typography sx={{ flex: 0.4, fontSize: '1.2rem', fontWeight: 700 }}>
                      Service Hours
                    </Typography>
                    <Stack direction="row" spacing={2} sx={{ flex: 1 }}>
                      <TimePicker
                        label="From"
                        value={dayjs(`2024-01-01T${watch('serviceHoursFrom') || '09:00'}`)}
                        onChange={(time) => handleTimeChange('serviceHoursFrom', time)}
                        disabled={!['new', 'edit'].includes(currentAction)}
                        sx={{ flex: 1 }}
                        slotProps={{ textField: { size: 'small' } }}
                      />
                      <TimePicker
                        label="To"
                        value={dayjs(`2024-01-01T${watch('serviceHoursTo') || '17:00'}`)}
                        onChange={(time) => handleTimeChange('serviceHoursTo', time)}
                        disabled={!['new', 'edit'].includes(currentAction)}
                        sx={{ flex: 1 }}
                        slotProps={{ textField: { size: 'small' } }}
                      />
                    </Stack>
                  </Stack>
                </Box>
              </Grid>

              {/* Row 7: Color Code and Icon */}
              <Grid item xs={6}>
                <Box sx={{ 
                  border: '1px solid gray', 
                  padding: 2, 
                  borderRadius: '8px',
                  boxShadow: '0px 2px 10px #0099ff' 
                }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Typography sx={{ flex: 0.4, fontSize: '1.2rem', fontWeight: 700 }}>
                      Color Code
                    </Typography>
                    <SelectTag
                      name="colorCode"
                      menus={COLOR_OPTIONS}
                      sx={{ 
                        flex: 1,
                        '& .MuiInputBase-input': { fontSize: '1.2rem', fontWeight: 600 }
                      }}
                      placeholder="Select Color"
                      disabled={!['new', 'edit'].includes(currentAction)}
                      allowClear={false}
                    />
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        backgroundColor: colorValue,
                        border: '2px solid #ccc',
                        borderRadius: '4px',
                        ml: 1
                      }}
                    />
                  </Stack>
                </Box>
              </Grid>

              <Grid item xs={6}>
                <Box sx={{ 
                  border: '1px solid gray', 
                  padding: 2, 
                  borderRadius: '8px',
                  boxShadow: '0px 2px 10px #0099ff' 
                }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Typography sx={{ flex: 0.4, fontSize: '1.2rem', fontWeight: 700 }}>
                      Icon
                    </Typography>
                    <SelectTag
                      name="icon"
                      menus={ICON_OPTIONS}
                      sx={{ 
                        flex: 1,
                        '& .MuiInputBase-input': { fontSize: '1.2rem', fontWeight: 600 }
                      }}
                      placeholder="Select Icon"
                      disabled={!['new', 'edit'].includes(currentAction)}
                      allowClear={false}
                    />
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#f5f5f5',
                        border: '2px solid #ccc',
                        borderRadius: '4px',
                        ml: 1
                      }}
                    >
                      <span className="material-icons">{iconValue}</span>
                    </Box>
                  </Stack>
                </Box>
              </Grid>
            </Grid>
          </FormProvider>
        </Container>
      </Paper>

      {/* Query Dialog */}
      <Dialog
        open={openQueryDialog}
        onClose={() => setOpenQueryDialog(false)}
        PaperProps={{ sx: { width: '90%', p: 1.5 } }}
        maxWidth="xl"
        scroll="body"
      >
        <Stack
          padding={0.2}
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          borderBottom="1px solid"
          borderColor="grey.300"
        >
          <Typography variant="h4" fontWeight="bold">
            Department Query Window
          </Typography>
          <IconButton onClick={() => setOpenQueryDialog(false)}>
            <CloseIcon color="error" fontSize="large" />
          </IconButton>
        </Stack>
        <Table
          sx={{ mt: 0.5 }}
          columns={TABLE_COLUMNS}
          data={departmentDataObj.data || []}
          loading={departmentDataObj.loading}
          onRowClick={(row) => {
            setOpenQueryDialog(false);
            setSelectedRecord(row);
            setCurrentAction();
          }}
        />
      </Dialog>
    </LocalizationProvider>
  );
};